import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import './styles/Snackbar.css';

const VARIANTS = {
  success: { icon: CheckCircleIcon, accent: '#22c55e' },
  error:   { icon: XCircleIcon,     accent: '#ef4444' },
  warning: { icon: ExclamationTriangleIcon, accent: '#f59e0b' },
  info:    { icon: InformationCircleIcon,   accent: '#3b82f6' },
};

/* ── Single snackbar ── */
const SnackbarItem = ({ id, message, variant = 'success', duration = 3500, onClose }) => {
  const { icon: Icon, accent } = VARIANTS[variant] || VARIANTS.info;

  useEffect(() => {
    const timer = setTimeout(() => onClose(id), duration);
    return () => clearTimeout(timer);
  }, [id, duration, onClose]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.96 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="snackbar-item"
      role="alert"
    >
      <span className="snackbar-accent" style={{ background: accent }} />
      <Icon className="snackbar-icon" style={{ color: accent }} />
      <span className="snackbar-message">{message}</span>
      <button className="snackbar-close" onClick={() => onClose(id)} aria-label="Dismiss">
        <XMarkIcon className="snackbar-close-icon" />
      </button>
    </motion.div>
  );
};

/* ── Container ── */
const SnackbarContainer = ({ snackbars, onClose }) => (
  <div className="snackbar-container" aria-label="Notifications">
    <AnimatePresence mode="popLayout">
      {snackbars.map((s) => (
        <SnackbarItem key={s.id} {...s} onClose={onClose} />
      ))}
    </AnimatePresence>
  </div>
);

export { SnackbarContainer, SnackbarItem };
export default SnackbarContainer;
